/**
 * Вкладка «Товары» — товары 1С, порождённые макетом.
 *
 * Поглотила половину бывшей вкладки «Связи»: та показывала те же товары, но с
 * бо́льшим числом данных, и рядом дублировала карточки маркетплейсов. Карточки
 * уехали в свою вкладку, здесь остались товары и комплектность.
 *
 * Усиление 11.10.2026: сводка-полоса (1С, файлы печати, витрина, визуалы) с
 * фильтром «только проблемные», превью товара, точки присутствия на площадках
 * вместо «2 шт.», группировка по носителю, недостающие позиции комплекта
 * строками с «Создать», выбор строк для «Переотправить фото».
 */

import React, { useMemo, useState } from 'react'
import { ExternalLink, ImageOff, MoreHorizontal, Plus, Send, X } from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { Button } from '../../../../components/ui/Button'
import { CopyButton } from '../../../../components/ui/CopyButton'
import { DropdownButton } from '../../../../components/ui/DropdownButton'
import { EmptyState } from '../../../../components/ui/EmptyState'
import { Skeleton } from '../../../../components/ui/Skeleton'
import { PhotoResendModal } from '../../../../components/marketplace/PhotoResendModal'
import type { PhotoResendInput } from '../../../../components/marketplace/photoResendApi'
import { copyToClipboard } from '../../../../components/ui/copyToClipboard'
import type { EntityTarget } from '../../../../components/entity-view/navigatorContext'
import { useToastStore } from '../../../../stores/toastStore'
import { cn } from '../../../../lib/cn'
import type { MarketplaceCardDto } from '../../../pipeline/api'
import type { RelationProduct } from '../../../entity-relations/types'
import type { ProductVisuals } from './MarketplaceTab'
import {
  PRESENCE_CHANNELS,
  PRESENCE_CHANNEL_LABEL,
  cardTarget,
  cardsTone,
  dotTitle,
  groupCards,
  isLiveCard,
  type PresenceChannel,
} from './designPresence'
import { SafeImg } from './parts'

export interface KitSummary {
  expected: number
  created: number
  isComplete: boolean
  missing: Array<{ description: string; missing: number }>
}

export interface ProductArticleMeta {
  prefix: string | null
  categoryName: string | null
}

interface ProductsTabProps {
  kit: KitSummary | null
  products: RelationProduct[] | null
  /** У каких артикулов уже есть файлы для печати */
  articlesWithPrintFiles: Set<string>
  error?: string | null
  /** Открыть товар окном поверх карточки, не уводя со страницы */
  onOpenProduct: (article: string) => void
  /** Карточки макета на площадках: из них точки «ВБ · ОЗ · ЯМ · Сайт». undefined — не передавались. */
  cards?: MarketplaceCardDto[] | null
  /** Визуалы товаров (есть не на всех стадиях). */
  visuals?: ProductVisuals[] | null
  /** Визуалы на этой стадии вообще бывают: иначе чип «Визуалы» честно говорит «нет данных». */
  visualsKnown?: boolean
  /** Превью макета — запасное превью строки. */
  designPreview?: string | null
  /** Префикс и название носителя по артикулу. */
  articleMeta?: Record<string, ProductArticleMeta>
  /** Создать недостающие товары комплекта. */
  onCreateMissing?: () => void
  createDisabledReason?: string
  creating?: boolean
  /** Право Card:UPDATE: «Переотправить фото». */
  canResendPhotos?: boolean
  /** Открыть карточку или черновик площадки окном поверх (через навигатор). */
  onOpenTarget?: (target: EntityTarget) => void
}

const ONE_C_RU: Record<string, { label: string; variant: 'gray' | 'yellow' | 'green' | 'red' }> = {
  synced: { label: 'В 1С', variant: 'green' },
  pending_1c: { label: 'Ждёт 1С', variant: 'yellow' },
  error: { label: 'Ошибка 1С', variant: 'red' },
}

type ProblemFilter = 'oneC' | 'print' | 'live' | 'visuals'

const DOT_CLASS = {
  ok: 'bg-emerald-500',
  bad: 'bg-red-500',
  warn: 'bg-amber-500',
  info: 'bg-indigo-500',
  none: 'border border-dashed border-gray-400 bg-transparent dark:border-zinc-500',
} as const

const GROUP_STORAGE_KEY = 'designCard.products.grouping'
type Grouping = 'flat' | 'carrier'

const readGrouping = (): Grouping => {
  try {
    return localStorage.getItem(GROUP_STORAGE_KEY) === 'carrier' ? 'carrier' : 'flat'
  } catch {
    return 'flat'
  }
}

const writeGrouping = (value: Grouping): void => {
  try {
    localStorage.setItem(GROUP_STORAGE_KEY, value)
  } catch {
    // приватный режим: окно просто забудет выбор
  }
}

const SummaryChip: React.FC<{
  label: string
  value: string
  tone: 'ok' | 'warn' | 'none'
  active: boolean
  onClick?: () => void
  title?: string
}> = ({ label, value, tone, active, onClick, title }) => {
  const body = (
    <>
      <span
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          tone === 'ok' ? DOT_CLASS.ok : tone === 'warn' ? DOT_CLASS.warn : DOT_CLASS.none,
        )}
      />
      <span>{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </>
  )
  const classes = cn(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
    tone === 'ok'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300'
        : 'border-gray-200 bg-gray-50 text-gray-500 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-400',
    active && 'ring-2 ring-indigo-500',
  )
  return onClick ? (
    <button type="button" aria-pressed={active} title={title} onClick={onClick} className={classes}>
      {body}
    </button>
  ) : (
    <span title={title} className={classes}>
      {body}
    </span>
  )
}

const TH =
  'whitespace-nowrap border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400'
const TD = 'border-b border-gray-100 px-3 py-2.5 dark:border-zinc-800'

export const ProductsTab: React.FC<ProductsTabProps> = ({
  kit,
  products,
  articlesWithPrintFiles,
  error,
  onOpenProduct,
  cards,
  visuals,
  visualsKnown = false,
  designPreview = null,
  articleMeta,
  onCreateMissing,
  createDisabledReason,
  creating = false,
  canResendPhotos = false,
  onOpenTarget,
}) => {
  const showToast = useToastStore((state) => state.showToast)
  const [filter, setFilter] = useState<ProblemFilter | null>(null)
  const [grouping, setGrouping] = useState<Grouping>(readGrouping)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [resendInput, setResendInput] = useState<PhotoResendInput | null>(null)
  const [resendOpen, setResendOpen] = useState(false)

  const cardsByArticle = useMemo(() => groupCards(cards ?? []), [cards])
  const visualsByArticle = useMemo(
    () => new Map((visuals ?? []).map((item) => [item.article, item.image_urls ?? []])),
    [visuals],
  )

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
        {error}
      </div>
    )
  }

  if (products === null) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton variant="custom" className="rounded-md" height={64} />
        <Skeleton variant="custom" className="rounded-md" height={220} />
      </div>
    )
  }

  // Карточки передали (даже если ещё грузятся): колонка «Площадки» вместо «N шт.»
  const cardsKnown = cards !== undefined
  const cardsLoaded = cards !== undefined && cards !== null
  const total = products.length

  const isOneCOk = (product: RelationProduct) => product.oneCStatus === 'synced' && !product.archived
  const isPrintOk = (product: RelationProduct) => articlesWithPrintFiles.has(product.article)
  const isLiveOk = (product: RelationProduct) => {
    const entry = cardsByArticle.get(product.article)
    return entry ? PRESENCE_CHANNELS.some((channel) => entry[channel].some(isLiveCard)) : false
  }
  const isVisualsOk = (product: RelationProduct) => (visualsByArticle.get(product.article)?.length ?? 0) > 0

  const counts = {
    oneC: products.filter(isOneCOk).length,
    print: products.filter(isPrintOk).length,
    live: products.filter(isLiveOk).length,
    visuals: products.filter(isVisualsOk).length,
  }
  const okOf: Record<ProblemFilter, (product: RelationProduct) => boolean> = {
    oneC: isOneCOk,
    print: isPrintOk,
    live: isLiveOk,
    visuals: isVisualsOk,
  }

  const visible = filter ? products.filter((product) => !okOf[filter](product)) : products
  const toneOf = (done: number): 'ok' | 'warn' => (total > 0 && done >= total ? 'ok' : 'warn')

  const changeGrouping = (value: Grouping) => {
    setGrouping(value)
    writeGrouping(value)
  }
  const toggleFilter = (value: ProblemFilter) => setFilter((current) => (current === value ? null : value))

  const allVisibleSelected = visible.length > 0 && visible.every((product) => selected.has(product.article))
  const toggleAll = () =>
    setSelected((current) => {
      const next = new Set(current)
      if (allVisibleSelected) visible.forEach((product) => next.delete(product.article))
      else visible.forEach((product) => next.add(product.article))
      return next
    })
  const toggleOne = (article: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(article)) next.delete(article)
      else next.add(article)
      return next
    })

  const openResend = (articles: string[]) => {
    setResendInput({ articles })
    setResendOpen(true)
  }
  const resendDisabledReason = canResendPhotos ? undefined : 'Нужно право на изменение карточек (Card:UPDATE)'
  const selectedArticles = products.map((product) => product.article).filter((article) => selected.has(article))

  const copyArticle = async (article: string) => {
    if (await copyToClipboard(article)) showToast('Скопировано: артикул', 'success')
    else showToast('Не удалось скопировать', 'error')
  }

  const previewOf = (product: RelationProduct): string | null => {
    const visual = visualsByArticle.get(product.article)?.[0]
    if (visual) return visual
    const entry = cardsByArticle.get(product.article)
    const fromCard = entry
      ? PRESENCE_CHANNELS.flatMap((channel) => entry[channel]).find((card) => card.preview_url)?.preview_url
      : null
    return fromCard ?? designPreview
  }

  const dots = (product: RelationProduct) => {
    const entry = cardsByArticle.get(product.article)
    return (
      <span className="inline-flex items-center gap-2" role="group" aria-label="Присутствие на площадках">
        {PRESENCE_CHANNELS.map((channel: PresenceChannel) => {
          const list = entry?.[channel] ?? []
          const tone = cardsTone(list)
          // Карточка одна — клик открывает её; несколько кабинетов — первую из них,
          // остальные видны в подсказке и на вкладке «Карточки МП».
          const target = list.length > 0 && onOpenTarget ? cardTarget(list.find(isLiveCard) ?? list[0]) : null
          const label = PRESENCE_CHANNEL_LABEL[channel]
          const inner = (
            <>
              <span className={cn('h-2 w-2 rounded-full', DOT_CLASS[tone])} aria-hidden="true" />
              <span className="text-2xs text-gray-600 dark:text-zinc-400">{label}</span>
            </>
          )
          const aria = `${label}: ${list.length === 0 ? 'нет карточки' : dotTitle(channel, list).replace(/\n/g, '; ')}`
          return target ? (
            <button
              key={channel}
              type="button"
              title={dotTitle(channel, list)}
              aria-label={aria}
              onClick={() => onOpenTarget?.(target)}
              className="inline-flex items-center gap-1 rounded px-0.5 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:bg-zinc-800"
            >
              {inner}
            </button>
          ) : (
            <span
              key={channel}
              title={dotTitle(channel, list)}
              aria-label={aria}
              className="inline-flex items-center gap-1 px-0.5"
            >
              {inner}
            </span>
          )
        })}
      </span>
    )
  }

  const renderRow = (product: RelationProduct) => {
    const oneC = ONE_C_RU[product.oneCStatus || ''] || null
    const legacyCards = product.wbCards.length + product.ozonCards.length + product.ymCards.length
    const hasFile = articlesWithPrintFiles.has(product.article)
    const preview = previewOf(product)
    return (
      <tr key={product.article} className="[&:last-child>td]:border-b-0">
        <td className={cn(TD, 'w-8')}>
          <input
            type="checkbox"
            aria-label={`Выбрать ${product.article}`}
            checked={selected.has(product.article)}
            onChange={() => toggleOne(product.article)}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-zinc-600"
          />
        </td>
        {cardsKnown && (
          <td className={cn(TD, 'w-12')}>
            <span className="flex h-12 w-10 items-center justify-center overflow-hidden rounded border border-gray-200 bg-white dark:border-zinc-700">
              {preview ? (
                <SafeImg
                  src={preview}
                  alt={`Превью ${product.article}`}
                  thumbnailWidth={96}
                  className="h-full w-full object-cover"
                  iconClass="h-3.5 w-3.5"
                />
              ) : (
                <ImageOff className="h-4 w-4 text-gray-300 dark:text-zinc-600" aria-hidden="true" />
              )}
            </span>
          </td>
        )}
        <td className={TD}>
          <span className="inline-flex items-center gap-0.5">
            <button
              type="button"
              title="Открыть карточку товара"
              onClick={() => onOpenProduct(product.article)}
              className="inline-flex items-center gap-1 font-mono text-[0.8125rem] font-medium text-indigo-700 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-indigo-300"
            >
              {product.article}
              <ExternalLink className="h-3 w-3" />
            </button>
            <CopyButton value={product.article} label="артикул" size="xs" />
          </span>
        </td>
        <td className={cn(TD, 'text-sm text-gray-700 dark:text-zinc-300')}>
          {product.category || product.title || '—'}
        </td>
        <td className={TD}>
          {product.archived ? (
            <Badge variant="red">Архив</Badge>
          ) : oneC ? (
            <Badge variant={oneC.variant}>{oneC.label}</Badge>
          ) : (
            <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
          )}
        </td>
        <td className={TD}>
          <span
            className={cn(
              'text-xs',
              hasFile ? 'text-green-700 dark:text-green-400' : 'text-gray-400 dark:text-zinc-500',
            )}
          >
            {hasFile ? 'Готов' : 'Нет'}
          </span>
        </td>
        <td className={cn(TD, 'text-xs text-gray-600 dark:text-zinc-400')}>
          {cardsKnown ? dots(product) : legacyCards > 0 ? `${legacyCards} шт.` : '—'}
        </td>
        <td className={cn(TD, 'w-10 text-right')}>
          <DropdownButton
            size="sm"
            align="right"
            portal
            showChevron={false}
            title={`Действия: ${product.article}`}
            icon={<MoreHorizontal className="h-4 w-4" />}
            buttonClassName="!px-1.5 !py-1"
          >
            {(close) => (
              <div className="py-1 text-sm">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  onClick={() => {
                    onOpenProduct(product.article)
                    close()
                  }}
                >
                  Открыть товар
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  onClick={() => {
                    void copyArticle(product.article)
                    close()
                  }}
                >
                  Скопировать артикул
                </button>
                <button
                  type="button"
                  disabled={Boolean(resendDisabledReason)}
                  title={resendDisabledReason}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  onClick={() => {
                    openResend([product.article])
                    close()
                  }}
                >
                  Переотправить фото
                </button>
              </div>
            )}
          </DropdownButton>
        </td>
      </tr>
    )
  }

  const columnCount = 7 + (cardsKnown ? 1 : 0)

  const groupedRows = (): React.ReactNode => {
    if (grouping === 'flat') return visible.map(renderRow)
    const groups = new Map<string, RelationProduct[]>()
    for (const product of visible) {
      const key = articleMeta?.[product.article]?.prefix ?? ''
      groups.set(key, [...(groups.get(key) ?? []), product])
    }
    return [...groups.entries()]
      .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b, 'ru')))
      .flatMap(([prefix, rows]) => {
        const name = rows.map((product) => articleMeta?.[product.article]?.categoryName).find(Boolean) ?? null
        return [
          <tr key={`group:${prefix}`}>
            <td
              colSpan={columnCount}
              className="border-b border-gray-200 bg-gray-50/80 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300"
            >
              <span className="font-mono">{prefix || 'Без категории'}</span>
              {name && <span className="ml-2 font-normal text-gray-500 dark:text-zinc-400">{name}</span>}
              <span className="ml-2 font-normal tabular-nums text-gray-400 dark:text-zinc-500">{rows.length}</span>
            </td>
          </tr>,
          ...rows.map(renderRow),
        ]
      })
  }

  const missingRows = kit && !kit.isComplete ? kit.missing : []

  return (
    <div className="space-y-4">
      {kit && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border p-3.5',
            kit.isComplete
              ? 'border-green-200 bg-green-50/60 dark:border-green-900 dark:bg-green-950/20'
              : 'border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20',
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
              {kit.isComplete
                ? `Комплект собран: ${kit.created} из ${kit.expected}`
                : `Создано ${kit.created} из ${kit.expected} товаров`}
            </div>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">
              Сколько товаров положено этому типу макета, решает матрица типов.
            </p>
            {!kit.isComplete && kit.missing.length > 0 && (
              <ul className="mt-1.5 space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
                {kit.missing.map((item) => (
                  <li key={item.description}>
                    Не хватает: {item.description} × {item.missing}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {total > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Сводка по товарам">
          <SummaryChip
            label="1С"
            value={`${counts.oneC}/${total}`}
            tone={toneOf(counts.oneC)}
            active={filter === 'oneC'}
            onClick={() => toggleFilter('oneC')}
            title="Показать товары, которых нет в 1С"
          />
          <SummaryChip
            label="Файлы печати"
            value={`${counts.print}/${total}`}
            tone={toneOf(counts.print)}
            active={filter === 'print'}
            onClick={() => toggleFilter('print')}
            title="Показать товары без файла печати"
          />
          {cardsLoaded ? (
            <SummaryChip
              label="На витрине"
              value={`${counts.live}/${total}`}
              tone={toneOf(counts.live)}
              active={filter === 'live'}
              onClick={() => toggleFilter('live')}
              title="Показать товары, которых нет на витрине ни одной площадки"
            />
          ) : null}
          {visualsKnown ? (
            <SummaryChip
              label="Визуалы"
              value={`${counts.visuals}/${total}`}
              tone={toneOf(counts.visuals)}
              active={filter === 'visuals'}
              onClick={() => toggleFilter('visuals')}
              title="Показать товары без визуалов"
            />
          ) : (
            <SummaryChip
              label="Визуалы"
              value="нет данных"
              tone="none"
              active={false}
              title="Нет данных: визуалы появляются на стадии после генерации"
            />
          )}
          <span
            role="group"
            aria-label="Группировка"
            className="ml-auto inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-800"
          >
            {([
              ['flat', 'Плоско'],
              ['carrier', 'По носителю'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={grouping === key}
                onClick={() => changeGrouping(key)}
                className={cn(
                  'rounded px-2 py-1 text-xs',
                  grouping === key
                    ? 'bg-white font-semibold text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                    : 'text-gray-500 hover:text-gray-800 dark:text-zinc-400 dark:hover:text-zinc-200',
                )}
              >
                {label}
              </button>
            ))}
          </span>
        </div>
      )}

      {filter && (
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-400">
          <span>Показаны только проблемные: {visible.length} из {total}.</span>
          <button
            type="button"
            onClick={() => setFilter(null)}
            className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline dark:text-indigo-400"
          >
            <X className="h-3 w-3" aria-hidden="true" />
            Показать все
          </button>
        </div>
      )}

      {selectedArticles.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2 dark:border-indigo-900 dark:bg-indigo-950/30">
          <span className="text-xs font-medium text-gray-800 dark:text-zinc-200">Выбрано: {selectedArticles.length}</span>
          <span title={resendDisabledReason}>
            <Button
              size="sm"
              variant="secondary"
              icon={<Send className="h-4 w-4" />}
              disabled={Boolean(resendDisabledReason)}
              onClick={() => openResend(selectedArticles)}
            >
              {`Переотправить фото (${selectedArticles.length})`}
            </Button>
          </span>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Снять выбор
          </button>
        </div>
      )}

      {products.length === 0 ? (
        <EmptyState
          title="Товаров пока нет"
          description={
            kit
              ? `По матрице типов макету положено товаров: ${kit.expected}. Создайте их кнопкой в полосе действий.`
              : 'Для типа этого макета не задан список обязательных товарных категорий.'
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800">
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className={cn(TH, 'w-8')}>
                    <input
                      type="checkbox"
                      aria-label="Выбрать все товары"
                      checked={allVisibleSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-zinc-600"
                    />
                  </th>
                  {cardsKnown && <th className={TH}>Превью</th>}
                  {['Артикул', 'Что это', 'Статус 1С', 'Файл печати', cardsKnown ? 'Площадки' : 'Карточки МП'].map(
                    (heading) => (
                      <th key={heading} className={TH}>
                        {heading}
                      </th>
                    ),
                  )}
                  <th className={TH}>
                    <span className="sr-only">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {groupedRows()}
                {missingRows.map((item) => (
                  <tr key={`missing:${item.description}`}>
                    <td
                      colSpan={columnCount - 1}
                      className="border-b border-dashed border-amber-300 bg-amber-50/50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300"
                    >
                      Не хватает: {item.description} × {item.missing}
                    </td>
                    <td className="border-b border-dashed border-amber-300 bg-amber-50/50 px-3 py-2 text-right dark:border-amber-900 dark:bg-amber-950/20">
                      {onCreateMissing && (
                        <span title={createDisabledReason ?? 'Создаст все недостающие товары макета'}>
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={<Plus className="h-4 w-4" />}
                            loading={creating}
                            disabled={Boolean(createDisabledReason)}
                            onClick={onCreateMissing}
                          >
                            Создать
                          </Button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {products.length === 0 && missingRows.length > 0 && onCreateMissing && (
        <div className="flex justify-center">
          <span title={createDisabledReason}>
            <Button
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              loading={creating}
              disabled={Boolean(createDisabledReason)}
              onClick={onCreateMissing}
            >
              Создать товары
            </Button>
          </span>
        </div>
      )}

      <PhotoResendModal isOpen={resendOpen} onClose={() => setResendOpen(false)} input={resendInput} />
    </div>
  )
}

export default ProductsTab
